// src/pages/CheckInAlert.tsx
import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import { collection, getDocs, doc, getDoc, query, where, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import CustomDropdown from "../components/CustomDropdown";
import {
  eachDayOfInterval,
  isSunday,
  parseISO,
  isAfter,
  isBefore,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  format,
} from "date-fns";
import { ArrowLeft, Calendar as CalendarIcon, RefreshCw } from "lucide-react";
import {
  useColorMode,
  Skeleton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
} from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";
import TimeSheetAlert from "./TimeSheetAlert";
import CheckInTable from "./CheckInTable";
import { fetchLeaveEntriesForUsers, DAY_PORTION_LABEL } from "../utils/leaveUtils";
import type { DayPortion } from "../utils/leaveUtils";
import { fetchCheckInRecordsForYear } from "../utils/checkInOutUtils";
import { fetchNonCheckInReasonsForYear, saveNonCheckInReason, updateNonCheckInReasonStatus, NonCheckInReasonEntry } from "../utils/nonCheckInReasonUtils";
import { fetchWfhEntriesForUsers } from "../utils/wfhUtils";
import type { WfhEntry } from "../utils/wfhUtils";
import { showToast } from "../utils/toastUtils";
import WfhApply from "../components/WfhApply/WfhApply";

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  joiningDate?: string; // ISO yyyy-MM-dd
}

interface CheckInAlertProps {
  organizationId: string;
  users: User[];
  initialUserId?: string; // optional: open directly to a specific user's 12-month view
  onBackToCalendar?: () => void; // optional: when provided, Back will return to CalendarPage
  hideBackButton?: boolean; // when true, the back button is hidden
  currentUser?: any;
  // when true, the viewer may click their own missed days to add/edit a
  // non-check-in reason. Only the My Check-Ins page passes this - the
  // admin-facing Calendar Alert view is read-only even when an admin is
  // looking at their own calendar.
  allowReasonEntry?: boolean;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Global memory cache to prevent redundant Firestore fetches on tab toggle/re-render
/** A half-filled dot: `side` is the half that stays coloured. */
const HalfDot: React.FC<{ className: string; side: "left" | "right" }> = ({
  className,
  side,
}) => (
  <span className={`relative inline-block w-3 h-3 rounded-full overflow-hidden align-middle shrink-0 ${className}`}>
    <span
      className={`absolute top-0 h-full w-1/2 bg-white ${side === "left" ? "right-0" : "left-0"}`}
    ></span>
  </span>
);

/**
 * Calendar legend. Kept as data so every row picks up the same spacing and
 * fluid text sizing, and so the leave entries stay next to their parents.
 */
const LEGEND_ITEMS: Array<{ key: string; swatch: React.ReactNode; label: string }> = [
  {
    key: "not-checked-in",
    swatch: <div className="w-3 h-3 bg-red-400 rounded-sm border shrink-0"></div>,
    label: "Not checked-in",
  },
  {
    key: "checked-in",
    swatch: <div className="w-3 h-3 bg-green-400 rounded-sm border shrink-0"></div>,
    label: "Checked-in",
  },
  {
    key: "auto-checkout",
    swatch: <div className="w-3 h-3 rounded-sm border-2 border-red-400 bg-transparent shrink-0"></div>,
    label: "Auto checkout",
  },
  {
    key: "half-day-checkin",
    swatch: <HalfDot className="bg-green-500" side="left" />,
    label: "Half Day Checkin",
  },
  {
    key: "holiday-checkins",
    swatch: <div className="w-3 h-3 bg-amber-400 rounded-sm border shrink-0"></div>,
    label: "Holiday CheckIns",
  },
  {
    key: "under-working",
    swatch: (
      <span className="text-red-500 text-[14px] leading-none font-extrabold select-none shrink-0">
        !
      </span>
    ),
    label: "Under working",
  },
  {
    key: "holiday",
    swatch: <div className="w-3 h-3 bg-blue-500 rounded-sm border shrink-0"></div>,
    label: "Holiday",
  },
  {
    key: "approved-leave",
    swatch: <div className="w-3 h-3 rounded-full bg-blue-500 shrink-0"></div>,
    label: "Approved Leave",
  },
  {
    key: "approved-half-day-leave",
    swatch: <HalfDot className="bg-blue-500 border border-blue-600" side="left" />,
    label: "Approved Half Day Leave",
  },
  {
    key: "pending-leave",
    swatch: <div className="w-3 h-3 rounded-full bg-orange-500 shrink-0"></div>,
    label: "Pending Leave",
  },
  {
    key: "pending-half-day-leave",
    swatch: <HalfDot className="bg-orange-500 border border-orange-600" side="left" />,
    label: "Pending Half Day Leave",
  },
  {
    key: "non-checkin-reason",
    swatch: <span className="inline-block w-[6px] h-[6px] bg-slate-500 rounded-full shrink-0"></span>,
    label: "Non Check-in Reason",
  },
  {
    key: "wfh",
    swatch: <div className="w-3 h-3 bg-purple-400 rounded-full border border-purple-500 shrink-0"></div>,
    label: "Remote Work",
  },
];

const checkInCache: {
  key: string | null;
  data: {
    missedCount: Record<string, number>;
    userTimes: Record<string, Date[]>;
    userAutoCheckoutDates: Record<string, string[]>;
    userMarkedAbsentDates: Record<string, string[]>;
    userDayRanges: Record<string, any>;
    holidays: Record<string, any>;
  } | null;
  timestamp: number;
} = {
  key: null,
  data: null,
  timestamp: 0,
};

export const invalidateCheckInCache = () => {
  checkInCache.key = null;
  checkInCache.data = null;
  checkInCache.timestamp = 0;
};

const CheckInAlert: React.FC<CheckInAlertProps> = ({
  organizationId,
  users,
  initialUserId,
  onBackToCalendar,
  hideBackButton,
  currentUser,
  allowReasonEntry = false,
}) => {
  const now = new Date();
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // View mode state
  const [viewMode, setViewMode] = useState<"CheckIn" | "TimeSheets" | "CheckInTable" | "WFH">("CheckIn");

  // Add calendar styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .big-calendar {
        width: 100% !important;
        font-size: 1.1em !important;
        border: none !important;
        background: ${isDark ? "#1A1C1F" : "white"} !important;
      }
      .big-calendar .react-calendar__navigation {
        background: ${isDark ? "#14161B" : "#f8fafc"} !important;
        border-bottom: 1px solid ${isDark ? "#2E2F33" : "#e2e8f0"} !important;
        margin-bottom: 0 !important;
        padding: 1rem !important;
      }
      .big-calendar .react-calendar__navigation button {
        color: ${isDark ? "#C3C9D3" : "#4a5568"} !important;
        font-weight: 600 !important;
        background: none !important;
        border: none !important;
        font-size: 1.1em !important;
      }
      .big-calendar .react-calendar__navigation button:hover {
        background: ${isDark ? "#2A3441" : "#e2e8f0"} !important;
        border-radius: 0.5rem !important;
      }
      .big-calendar .react-calendar__month-view__weekdays {
        background: ${isDark ? "#1A1C1F" : "#f1f5f9"} !important;
        font-weight: 600 !important;
        color: ${isDark ? "#C3C9D3" : "#64748b"} !important;
        font-size: 0.875rem !important;
        text-transform: uppercase !important;
        letter-spacing: 0.05em !important;
        padding: 0.75rem 0 !important;
        border-bottom: 1px solid ${isDark ? "#2E2F33" : "#e2e8f0"} !important;
      }
      .big-calendar .react-calendar__month-view__weekdays__weekday {
        padding: 0.5rem !important;
        text-align: center !important;
      }
      .big-calendar .react-calendar__month-view__days {
        background: ${isDark ? "#1A1C1F" : "white"} !important;
        color: ${isDark ? '#FFFFFF' : '#374151'} !important;
      }
      .big-calendar .react-calendar__tile {
        position: relative !important;
        background: ${isDark ? "#14161B" : "#ffffff"} !important; /* mirror TimeSheetAlert */
        border: 1px solid ${isDark ? "#2E2F33" : "#e5e7eb"} !important; /* subtle separators like TimeSheetAlert */
        color: ${isDark ? "#FFFFFF" : "#374151"} !important;
        font-weight: 500 !important;
        height: 3.5rem !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.2s ease !important;
      }
      .big-calendar .react-calendar__tile:hover {
        background: ${isDark ? "#2A3441" : "#f8fafc"} !important;
        transform: translateY(-1px) !important;
        box-shadow: ${isDark ? "none" : "0 2px 4px rgba(0,0,0,0.1)"} !important;
      }
      .big-calendar .react-calendar__tile--now {
        background: ${isDark ? "#3A4553" : "#fef3c7"} !important;
        color: ${isDark ? "#FBD282" : "#92400e"} !important;
        font-weight: 700 !important;
        border: 2px solid ${isDark ? "#B59143" : "#f59e0b"} !important;
      }
      .big-calendar .react-calendar__month-view__days__day--sunday {
        color: #dc2626 !important;
        background: ${isDark ? "#2A3441" : "#fef2f2"} !important;
        border-color: ${isDark ? "#7f1d1d" : "#fecaca"} !important;
      }
      .big-calendar .react-calendar__month-view__days__day--weekend:not(.react-calendar__month-view__days__day--sunday) {
        color: #374151 !important;
      }
      .big-calendar .react-calendar__tile--active {
        background: ${isDark ? "#2A3441" : "#FFF7E6"} !important;
        color: ${isDark ? "#FBD282" : "#B59143"} !important;
        border-color: ${isDark ? "#B59143" : "#E8C16C"} !important;
      }
      .big-calendar .react-calendar__tile--active:hover {
        background: ${isDark ? "#3A4553" : "#FAEDD0"} !important;
      }
      .year-calendar {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1rem;
        max-width: none;
      }
      @media (max-width: 1024px) {
        .year-calendar {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      @media (max-width: 640px) {
        .year-calendar {
          grid-template-columns: 1fr;
        }
      }
      .month-calendar {
        background: ${isDark ? "#1A1C1F" : "white"};
        border-radius: 0.5rem;
        box-shadow: ${isDark ? "none" : "0 1px 2px rgba(0, 0, 0, 0.05)"};
        overflow: hidden;
        border: 1px solid ${isDark ? "#2E2F33" : "#e2e8f0"};
      }
      .month-calendar.current-month {
        border: 1px solid #22c55e;
        box-shadow: 0 1px 3px rgba(34, 197, 94, 0.15);
      }
      .month-calendar-header {
        background: ${isDark ? "#14161B" : "#f8fafc"};
        padding: 0.5rem;
        border-bottom: 1px solid ${isDark ? "#2E2F33" : "#e2e8f0"};
        text-align: center;
        font-weight: 700;
        color: ${isDark ? "#FFFFFF" : "#374151"};
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      .month-calendar .react-calendar {
        width: 100% !important;
        border: none !important;
        font-size: 0.8rem !important;
      }
      .month-calendar .react-calendar__navigation {
        display: none !important;
      }
      .month-calendar .react-calendar__tile {
        height: 2rem !important;
        font-size: 0.9rem !important;
        color: ${isDark ? "#FFFFFF" : "#111827"} !important;
        font-weight: 500 !important;
        border: 1px solid ${isDark ? "#2E2F33" : "#f1f5f9"} !important;
        background: ${isDark ? "#14161B" : "#ffffff"} !important;
        position: relative !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
      .month-calendar .react-calendar__month-view__weekdays {
        font-size: 0.75rem !important;
        padding: 0.5rem 0 !important;
        color: ${isDark ? '#C3C9D3' : '#64748b'} !important;
        font-weight: 600 !important;
      }
      .month-calendar .react-calendar__tile abbr {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: auto !important;
        height: auto !important;
        border-radius: 0 !important;
        background: transparent !important;
        transition: color 0.15s ease !important;
        padding: 0 !important;
      }
      .month-calendar .react-calendar__tile--missed abbr {
        background: transparent !important;
        color: #dc2626 !important; /* red-600 */
        font-weight: 700 !important;
      }
      .month-calendar .react-calendar__tile--checkedin abbr {
        background: transparent !important;
        color: #16a34a !important; /* green-600 */
        font-weight: 700 !important;
      }
      .month-calendar .react-calendar__tile--sundaycheckedin abbr {
        background: transparent !important;
        color: #f59e0b !important; /* amber-500 for Sunday check-in */
        border: none !important;
        font-weight: 700 !important;
      }
      .month-calendar .react-calendar__tile--holidaycheckedin abbr {
        background: transparent !important;
        color: #b45309 !important; /* amber-700 for holiday check-in */
        border: none !important;
        font-weight: 700 !important;
      }
      /* Holiday styling - blue circular background covering the day number */
      .month-calendar .react-calendar__tile--holiday abbr {
        background: ${isDark ? "#B59143" : "#3b82f6"} !important;
        color: white !important;
        border: none !important;
        border-radius: 50% !important;
        font-weight: 600 !important;
        padding: 0 !important;
        width: 24px !important;
        height: 24px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 0.8em !important;
      }
      /* Auto checkout shows a small red square border around the day number */
      .month-calendar .autocheckout-ring abbr {
        border: 1px solid #ef4444 !important; /* red-500 */
        border-radius: 0 !important;
        padding: 0 4px !important;
      }
      /* Approved leave styling - simple blue background with white text */
      .month-calendar .react-calendar__tile--approvedleave abbr {
        background: ${isDark ? "#B59143" : "#3b82f6"} !important; /* use accent in dark */
        color: white !important;
        border: none !important;
        border-radius: 50% !important;
        font-weight: 600 !important;
        padding: 0 !important;
        width: 24px !important;
        height: 24px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 0.8em !important;
      }
      /* Pending leave styling - orange circular background with white text */
      .month-calendar .react-calendar__tile--pendingleave abbr {
        background: #f97316 !important; /* orange-500 for pending */
        color: white !important;
        border: none !important;
        border-radius: 50% !important;
        font-weight: 600 !important;
        padding: 0 !important;
        width: 24px !important;
        height: 24px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 0.8em !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isDark]);

  // dropdown‐driven month and year
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // Default to current month
  const [selectedYear, setSelectedYear] = useState(now.getFullYear()); // Default to current year

  // user → missed‐days count
  const [missedCount, setMissedCount] = useState<Record<string, number>>({});

  // user → all check-in times
  const [userTimes, setUserTimes] = useState<Record<string, Date[]>>({});
  // user → auto checkout dates (yyyy-MM-dd)
  const [userAutoCheckoutDates, setUserAutoCheckoutDates] = useState<
    Record<string, string[]>
  >({});
  // user → dates flagged markedAsAbsent (yyyy-MM-dd) - auto-checked-out days
  // that should read as absent on the My Check-Ins calendar
  const [userMarkedAbsentDates, setUserMarkedAbsentDates] = useState<
    Record<string, string[]>
  >({});

  // user → per-day time ranges { start, end, totalMs, workMode, workFromHomeApproval }
  const [userDayRanges, setUserDayRanges] = useState<
    Record<string, Record<string, { start: Date | null; end: Date | null; totalMs: number | null; workMode?: string; workFromHomeApproval?: string }>>
  >({});

  // holidays data
  const [holidays, setHolidays] = useState<Record<string, any>>({});

  // approved leave data for each user
  const [userApprovedLeaves, setUserApprovedLeaves] = useState<Record<string, any[]>>({});
  // pending leave data for each user
  const [userPendingLeaves, setUserPendingLeaves] = useState<Record<string, any[]>>({});

  // non check-in reasons for the calendar owner, keyed by date (yyyy-MM-dd)
  const [nonCheckInReasons, setNonCheckInReasons] = useState<Record<string, NonCheckInReasonEntry>>({});

  // WFH data for calendar indicators: user -> approved/pending entries
  const [userApprovedWfh, setUserApprovedWfh] = useState<Record<string, WfhEntry[]>>({});
  const [userPendingWfh, setUserPendingWfh] = useState<Record<string, WfhEntry[]>>({});
  const [reasonModalDate, setReasonModalDate] = useState<Date | null>(null);
  const [reasonInput, setReasonInput] = useState("");
  const [savingReason, setSavingReason] = useState(false);

  // Admin approval modal state
  const [approvalModalDate, setApprovalModalDate] = useState<Date | null>(null);
  const [approvalReasonEntry, setApprovalReasonEntry] = useState<NonCheckInReasonEntry | null>(null);
  const [approving, setApproving] = useState(false);

  const [loading, setLoading] = useState(false);

  // which user's calendar we're viewing
  const [calendarUserId, setCalendarUserId] = useState<string | null>(
    initialUserId ?? null
  );

  // Calendar year (defaults to current year)
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());

  // Search state
  const [searchQuery, setSearchQuery] = useState("");



  // drives the Calendar's displayed month/year
  const [activeDate, setActiveDate] = useState<Date>(
    startOfMonth(new Date(selectedYear, selectedMonth - 1))
  );

  // Generate year options (2025 to current year only)
  const yearOptions = [];
  const currentYear = now.getFullYear();
  for (let year = 2025; year <= currentYear; year++) {
    yearOptions.push(year);
  }

  // Fetch holidays data
  // fetch data on mount or month/year change
  const fetchData = async (forceRefresh = false) => {
    const year = calendarUserId ? calendarYear : selectedYear;
    const cacheKey = `${organizationId}-${year}-${selectedMonth}-${users.map((u) => u.id).join(",")}`;
    const cacheAge = Date.now() - checkInCache.timestamp;
    const hasCachedCheckInData =
      !forceRefresh && checkInCache.key === cacheKey && !!checkInCache.data && cacheAge < 10 * 60 * 1000;

    // checkInCache is a module-level object, so it survives a sign-out/sign-in
    // (no page reload) even though this component's own state (holidays,
    // userApprovedLeaves, userPendingLeaves) resets to empty on every fresh
    // mount. A cache hit here only covers missed/times/auto-checkout/day-range
    // data - it must NOT short-circuit the holiday/leave fetches below, or a
    // freshly-mounted instance never populates them (this was the bug behind
    // leaves silently failing to load after sign-in while the calendar itself
    // looked fine).
    if (hasCachedCheckInData) {
      setMissedCount(checkInCache.data!.missedCount);
      setUserTimes(checkInCache.data!.userTimes);
      setUserAutoCheckoutDates(checkInCache.data!.userAutoCheckoutDates);
      setUserMarkedAbsentDates(checkInCache.data!.userMarkedAbsentDates);
      setUserDayRanges(checkInCache.data!.userDayRanges);
      setHolidays(checkInCache.data!.holidays);
    }

    setLoading(true);

    // 1. Fetch holidays if not loaded or forceRefresh
    let activeHolidays = holidays;
    if (Object.keys(activeHolidays).length === 0 || forceRefresh) {
      try {
        const holidaysCol = collection(
          db,
          "organization",
          organizationId,
          "holidays"
        );
        const holidaysSnap = await getDocs(holidaysCol);
        const holidaysData: Record<string, any> = {};
        holidaysSnap.forEach((doc) => {
          const data = doc.data();
          holidaysData[doc.id] = {
            id: doc.id,
            date: data.date,
            reason: data.reason,
          };
        });
        setHolidays(holidaysData);
        activeHolidays = holidaysData;
      } catch (error) {
        console.error("❌ Error fetching holidays:", error);
      }
    }

    // 2. Fetch approved & pending leaves if not loaded or forceRefresh
    let activeLeaves = userApprovedLeaves;
    if (Object.keys(activeLeaves).length === 0 || forceRefresh) {
      console.log("[Leaves] fetch starting", {
        organizationId,
        userCount: users.length,
        userIds: users.map((u) => u.id),
      });
      try {
        const allEntriesByUser = await fetchLeaveEntriesForUsers(
          organizationId,
          users.map(u => u.id)
        );

        const totalRawEntries = Object.values(allEntriesByUser).reduce((acc, arr) => acc + arr.length, 0);
        const distinctStatuses = Array.from(
          new Set(Object.values(allEntriesByUser).flat().map((e: any) => JSON.stringify(e.approvalStatus)))
        );
        console.log("[Leaves] raw fetch result", {
          totalRawEntries,
          entriesPerUser: Object.fromEntries(
            Object.entries(allEntriesByUser).filter(([, arr]) => arr.length > 0).map(([uid, arr]) => [uid, arr.length])
          ),
          distinctApprovalStatusValues: distinctStatuses,
        });

        const approvedLeavesData: Record<string, any[]> = {};
        const pendingLeavesData: Record<string, any[]> = {};
        Object.entries(allEntriesByUser).forEach(([uid, entries]) => {
          approvedLeavesData[uid] = entries.filter((e: any) =>
            e.approvalStatus === "Approved" || e.approvalStatus === "approved"
          );
          pendingLeavesData[uid] = entries.filter((e: any) =>
            e.approvalStatus === "Pending" || e.approvalStatus === "pending"
          );
        });

        const totalApproved = Object.values(approvedLeavesData).reduce((acc, arr) => acc + arr.length, 0);
        const totalPending = Object.values(pendingLeavesData).reduce((acc, arr) => acc + arr.length, 0);
        console.log("[Leaves] filtered result", { totalApproved, totalPending });

        setUserApprovedLeaves(approvedLeavesData);
        setUserPendingLeaves(pendingLeavesData);
        activeLeaves = approvedLeavesData;
      } catch (error) {
        console.error("❌ Error fetching leaves:", error);
      }
    } else {
      console.log("[Leaves] skipped fetch - already loaded", { keys: Object.keys(activeLeaves).length });
    }

    // 3. Fetch approved & pending WFH entries from WorkFromHomeData
    try {
      const allWfhByUser = await fetchWfhEntriesForUsers(
        organizationId,
        users.map(u => u.id)
      );

      const approvedWfhData: Record<string, WfhEntry[]> = {};
      const pendingWfhData: Record<string, WfhEntry[]> = {};
      Object.entries(allWfhByUser).forEach(([uid, entries]) => {
        approvedWfhData[uid] = entries.filter((e: any) => {
          const s = (e.approvalStatus || "").toLowerCase();
          return s === "approved";
        });
        pendingWfhData[uid] = entries.filter((e: any) => {
          const s = (e.approvalStatus || "").toLowerCase();
          return s === "pending";
        });
      });

      setUserApprovedWfh(approvedWfhData);
      setUserPendingWfh(pendingWfhData);
    } catch (error) {
      console.error("Error fetching Remote Work entries:", error);
    }

    // The check-in/attendance data itself was already restored from
    // checkInCache above - holidays and leaves are freshly fetched
    // regardless, but there's no need to redo the (expensive) per-user
    // check-in record fetch below.
    if (hasCachedCheckInData) {
      setLoading(false);
      return;
    }

    let start: Date;
    let end: Date;

    // If no month selected (month = 0), calculate for entire year up to present
    if (selectedMonth === 0) {
      start = startOfYear(new Date(year, 0));
      end = year === now.getFullYear() ? now : endOfYear(start);
    } else {
      const mIdx = selectedMonth - 1;
      start = startOfMonth(new Date(year, mIdx));
      end =
        mIdx === now.getMonth() && year === now.getFullYear()
          ? now
          : endOfMonth(start);
    }

    const counts: Record<string, number> = {};
    const timesMap: Record<string, Date[]> = {};
    const autoMap: Record<string, string[]> = {};
    const markedAbsentMap: Record<string, string[]> = {};
    const dayRangesMap: Record<string, Record<string, { start: Date | null; end: Date | null; totalMs: number | null; workMode?: string; workFromHomeApproval?: string }>> = {};

    // Batch process all users for better performance (joining-date aware)
    const userPromises = users.map(async (u) => {
      try {
        // Fetch check-in records from partitioned month subcollections
        const allRecords = await fetchCheckInRecordsForYear(organizationId, u.id, year);
        const docsData = allRecords;

        const rawTimes = docsData
          .map((d) => d.checkInTime as string)
          .filter((timeStr) => timeStr);

        const times = rawTimes
          .map((s) => {
            try {
              return parseISO(s);
            } catch (error) {
              console.error(`❌ Failed to parse date "${s}":`, error);
              return null;
            }
          })
          .filter((date) => date !== null) as Date[];

        // Build per-day ranges
        const perDay: Record<string, { start: Date | null; end: Date | null; totalMs: number | null; workMode?: string; workFromHomeApproval?: string }> = {};
        docsData.forEach((d: any) => {
          if (!d.checkInTime) return;
          let startDate: Date | null = null;
          let endDate: Date | null = null;
          try {
            startDate = parseISO(d.checkInTime as string);
          } catch {
            startDate = null;
          }
          if (d.checkOutTime) {
            try {
              endDate = parseISO(d.checkOutTime as string);
            } catch {
              endDate = null;
            }
          } else {
            endDate = null;
          }
          if (!startDate) return;
          const key = format(startDate, "yyyy-MM-dd");
          const existing = perDay[key] || { start: null, end: null, totalMs: null };
          // choose earliest start and latest end
          const earliestStart = existing.start && startDate ? (existing.start < startDate ? existing.start : startDate) : startDate || existing.start;
          const latestEnd = existing.end && endDate ? (existing.end > endDate ? existing.end : endDate) : endDate || existing.end;
          let totalMs: number | null = null;
          if (earliestStart && latestEnd) {
            totalMs = Math.max(0, latestEnd.getTime() - earliestStart.getTime());
          }
          perDay[key] = { start: earliestStart, end: latestEnd, totalMs, workMode: d.workMode || "Office", workFromHomeApproval: d.workFromHomeApproval };
        });

        // Checked-in keys within period
        const checkedKeySet = new Set(
          times
            .filter(
              (dt) =>
                (isAfter(dt, start) ||
                  dt.toDateString() === start.toDateString()) &&
                (isBefore(dt, end) || dt.toDateString() === end.toDateString())
            )
            .map((dt) => format(dt, "yyyy-MM-dd"))
        );

        // Determine user-specific business days within the month range starting from joining date
        let userStart = start;
        if (u.joiningDate) {
          try {
            const jd = parseISO(u.joiningDate);
            if (!isNaN(jd.getTime())) {
              // If user joined after the period end, they have 0 missed days for this period
              if (isAfter(jd, end)) {
                return {
                  userId: u.id,
                  times,
                  missedCount: 0,
                  autoDates: [],
                  markedAbsentDates: [],
                  perDay: {},
                };
              }
              // If user joined during this period, start counting from joining date
              if (isAfter(jd, userStart)) userStart = jd;
            }
          } catch { }
        }
        const userBizKeys = eachDayOfInterval({ start: userStart, end })
          .filter((d) => {
            const dateKey = format(d, "yyyy-MM-dd");
            return !isSunday(d) && !activeHolidays[dateKey];
          })
          .map((d) => format(d, "yyyy-MM-dd"));

        const inPeriodCheckedCount = userBizKeys.reduce(
          (acc, k) => acc + (checkedKeySet.has(k) ? 1 : 0),
          0
        );

        // Collect auto-checkout dates keyed by check-in date
        const autoDates = docsData
          .filter((d) => d.checkOutAddress === "autocheckout" && d.checkInTime)
          .map((d) => {
            try {
              return format(parseISO(d.checkInTime as string), "yyyy-MM-dd");
            } catch {
              return null;
            }
          })
          .filter((x) => !!x) as string[];

        // Collect markedAsAbsent dates keyed by check-in date
        const markedAbsentDates = docsData
          .filter((d) => d.markedAsAbsent === true && d.checkInTime)
          .map((d) => {
            try {
              return format(parseISO(d.checkInTime as string), "yyyy-MM-dd");
            } catch {
              return null;
            }
          })
          .filter((x) => !!x) as string[];

        // Calculate approved leave days within the period
        const userLeaves = activeLeaves[u.id] || [];
        const leaveDaysInPeriod = new Set<string>();

        userLeaves.forEach((leave: any) => {
          const fromDate = new Date(leave.fromDate);
          const toDate = new Date(leave.toDate);

          // Generate all dates in the leave period
          const leaveDates = eachDayOfInterval({ start: fromDate, end: toDate });

          leaveDates.forEach(leaveDate => {
            const leaveDateKey = format(leaveDate, "yyyy-MM-dd");
            // Only count leave days that fall within business days for the user
            if (userBizKeys.includes(leaveDateKey)) {
              leaveDaysInPeriod.add(leaveDateKey);
            }
          });
        });

        const rawMissed = userBizKeys.length - inPeriodCheckedCount - leaveDaysInPeriod.size;
        const adjustedMissed = Math.max(0, rawMissed);

        return {
          userId: u.id,
          times,
          missedCount: adjustedMissed,
          autoDates,
          markedAbsentDates,
          perDay,
        };
      } catch (error) {
        console.error(`❌ Error fetching check-ins for user ${u.id}:`, error);
        return {
          userId: u.id,
          times: [],
          missedCount: 0,
          autoDates: [],
          markedAbsentDates: [],
          perDay: {},
        };
      }
    });

    // Wait for all user queries to complete
    const results = await Promise.all(userPromises);

    // Process results
    results.forEach((result) => {
      counts[result.userId] = result.missedCount;
      timesMap[result.userId] = result.times;
      autoMap[result.userId] = result.autoDates;
      markedAbsentMap[result.userId] = result.markedAbsentDates;
      dayRangesMap[result.userId] = result.perDay;
    });


    checkInCache.key = cacheKey;
    checkInCache.data = {
      missedCount: counts,
      userTimes: timesMap,
      userAutoCheckoutDates: autoMap,
      userMarkedAbsentDates: markedAbsentMap,
      userDayRanges: dayRangesMap,
      holidays: activeHolidays,
    };
    checkInCache.timestamp = Date.now();

    setMissedCount(counts);
    setUserTimes(timesMap);
    setUserAutoCheckoutDates(autoMap);
    setUserMarkedAbsentDates(markedAbsentMap);
    setUserDayRanges(dayRangesMap);
    setLoading(false);

  };

  useEffect(() => {
    // Only fetch when the CheckIn tab is active
    if (viewMode !== "CheckIn") return;

    let alive = true;

    (async () => {
      setLoading(true);
      await fetchData();
      if (!alive) return;
      setLoading(false);
      if (selectedMonth > 0) {
        setActiveDate(startOfMonth(new Date(selectedYear, selectedMonth - 1)));
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedMonth, selectedYear, calendarYear, calendarUserId, organizationId, users, viewMode]);

  // Fetch non-check-in reasons for whichever calendar is being viewed.
  // Viewable in both Calendar Alert (admin, read-only) and My Check-Ins;
  // whether they can be *added/edited* is gated separately (allowReasonEntry).
  useEffect(() => {
    if (!calendarUserId) {
      setNonCheckInReasons({});
      return;
    }
    let alive = true;
    (async () => {
      try {
        const data = await fetchNonCheckInReasonsForYear(organizationId, calendarUserId, calendarYear);
        if (alive) setNonCheckInReasons(data);
      } catch (error) {
        console.error("❌ Error fetching non check-in reasons:", error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [calendarUserId, calendarYear, organizationId]);

  // sort users alphabetically by name
  const sortedUsers = [...users].sort((a, b) => {
    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Filter users based on search query and joining date
  const filteredUsers = sortedUsers.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      user.firstName.toLowerCase().includes(searchLower) ||
      user.lastName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower);

    // Filter out users whose joining date is after the selected period
    if (user.joiningDate) {
      try {
        const joiningDate = parseISO(user.joiningDate);
        // Calculate the period end based on selected month and year
        const periodEnd = selectedMonth === 0
          ? (selectedYear === now.getFullYear() ? now : endOfYear(new Date(selectedYear, 0)))
          : (selectedMonth - 1 === now.getMonth() && selectedYear === now.getFullYear()
            ? now
            : endOfMonth(new Date(selectedYear, selectedMonth - 1)));

        if (!isNaN(joiningDate.getTime()) && isAfter(joiningDate, periodEnd)) {
          return false; // Don't show users who joined after the period
        }
      } catch {
        // If parsing fails, include the user
      }
    }

    return matchesSearch;
  });



  // Helper function to get check-in status for a date
  const getCheckInStatus = (date: Date, userId: string) => {
    const times = userTimes[userId] || [];
    const checkedSet = new Set(times.map((dt) => dt.toDateString()));
    return checkedSet.has(date.toDateString());
  };
  // Was this date auto-checked-out (based on check-in date)?
  const isAutoCheckoutDate = (date: Date, userId: string) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const arr = userAutoCheckoutDates[userId] || [];
    return new Set(arr).has(dateKey);
  };
  // Was this date flagged markedAsAbsent (based on check-in date)?
  const isMarkedAbsentDate = (date: Date, userId: string) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const arr = userMarkedAbsentDates[userId] || [];
    return new Set(arr).has(dateKey);
  };
  // In My Check-Ins (allowReasonEntry), a markedAsAbsent day should read as
  // absent rather than checked-in. The admin-facing Calendar Alert view is
  // left as-is.
  const isEffectivelyCheckedIn = (date: Date, userId: string) => {
    const checkedIn = getCheckInStatus(date, userId);
    if (checkedIn && allowReasonEntry && isMarkedAbsentDate(date, userId)) return false;
    return checkedIn;
  };

  // Helper function to check if a date is a holiday
  const isHoliday = (date: Date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    return !!holidays[dateKey];
  };

  // Find the leave entry covering a date, from either the approved or the
  // pending set. All the leave helpers below read from this.
  const findLeaveOnDate = (
    source: Record<string, any[]>,
    date: Date,
    userId: string
  ) => {
    const userLeaves = source[userId] || [];
    const dateKey = format(date, "yyyy-MM-dd");
    const checkDate = new Date(dateKey);

    return (
      userLeaves.find((leave: any) => {
        const fromDate = new Date(leave.fromDate);
        const toDate = new Date(leave.toDate);
        return checkDate >= fromDate && checkDate <= toDate;
      }) || null
    );
  };

  // Which part of the day a leave covers: full day, or one of the halves.
  // Entries created before half-day support have no dayPortions map and are
  // treated as full days.
  const getLeavePortionFrom = (
    source: Record<string, any[]>,
    date: Date,
    userId: string
  ): DayPortion | null => {
    const leave = findLeaveOnDate(source, date, userId);
    if (!leave) return null;
    const dateKey = format(date, "yyyy-MM-dd");
    return (leave.dayPortions?.[dateKey] as DayPortion) || "full";
  };

  // Helper function to check if a date is an approved leave date for a user
  const isApprovedLeave = (date: Date, userId: string) =>
    !!findLeaveOnDate(userApprovedLeaves, date, userId);

  // Helper function to get leave reason for a specific date
  const getLeaveReason = (date: Date, userId: string) =>
    findLeaveOnDate(userApprovedLeaves, date, userId)?.reason ?? null;

  // Helper function to check if a date is a pending leave date for a user
  const isPendingLeave = (date: Date, userId: string) =>
    !!findLeaveOnDate(userPendingLeaves, date, userId);

  // Helper function to get pending leave reason for a specific date
  const getPendingLeaveReason = (date: Date, userId: string) =>
    findLeaveOnDate(userPendingLeaves, date, userId)?.reason ?? null;

  const getApprovedLeavePortion = (date: Date, userId: string) =>
    getLeavePortionFrom(userApprovedLeaves, date, userId);

  const getPendingLeavePortion = (date: Date, userId: string) =>
    getLeavePortionFrom(userPendingLeaves, date, userId);

  // WFH helpers: check WorkFromHomeData entries for a given date
  const findWfhOnDate = (
    source: Record<string, WfhEntry[]>,
    date: Date,
    userId: string
  ) => {
    const userWfh = source[userId] || [];
    const dateKey = format(date, "yyyy-MM-dd");
    const checkDate = new Date(dateKey);
    return (
      userWfh.find((entry: WfhEntry) => {
        const fromDate = new Date(entry.fromDate);
        const toDate = new Date(entry.toDate);
        return checkDate >= fromDate && checkDate <= toDate;
      }) || null
    );
  };

  const isApprovedWfh = (date: Date, userId: string) =>
    !!findWfhOnDate(userApprovedWfh, date, userId);

  const isPendingWfh = (date: Date, userId: string) =>
    !!findWfhOnDate(userPendingWfh, date, userId);

  const getWfhReason = (date: Date, userId: string) =>
    findWfhOnDate(userApprovedWfh, date, userId)?.reason ??
    findWfhOnDate(userPendingWfh, date, userId)?.reason ??
    null;

  // A day is eligible for a "reason" prompt when it's a past/today business
  // day the user simply didn't check in on - not a Sunday, holiday, or leave.
  const isMissedDay = (date: Date, userId: string) => {
    if (date > now) return false;
    if (isSunday(date)) return false;
    if (isHoliday(date)) return false;
    if (isApprovedLeave(date, userId)) return false;
    if (isPendingLeave(date, userId)) return false;
    if (isApprovedWfh(date, userId)) return false;
    if (isEffectivelyCheckedIn(date, userId)) return false;
    return true;
  };

  const openReasonModal = (date: Date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    setReasonInput(nonCheckInReasons[dateKey]?.reason || "");
    setReasonModalDate(date);
  };

  const handleSaveReason = async () => {
    if (!reasonModalDate || !calendarUserId || !reasonInput.trim()) return;
    setSavingReason(true);
    try {
      await saveNonCheckInReason(organizationId, calendarUserId, reasonModalDate, reasonInput.trim());
      const dateKey = format(reasonModalDate, "yyyy-MM-dd");
      setNonCheckInReasons((prev) => ({
        ...prev,
        [dateKey]: {
          date: dateKey,
          reason: reasonInput.trim(),
          userId: calendarUserId,
          timestamp: new Date().toISOString(),
          approvalStatus: "Pending",
        },
      }));
      showToast({ title: "Reason saved", status: "success" });
      setReasonModalDate(null);
    } catch (error) {
      console.error("❌ Error saving non check-in reason:", error);
      showToast({ title: "Failed to save reason", status: "error" });
    } finally {
      setSavingReason(false);
    }
  };

  const openApprovalModal = (date: Date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const entry = nonCheckInReasons[dateKey];
    if (entry) {
      setApprovalReasonEntry(entry);
      setApprovalModalDate(date);
    }
  };

  const handleApproveReject = async (status: "Approved" | "Rejected") => {
    if (!approvalModalDate || !approvalReasonEntry || !currentUser) return;
    setApproving(true);
    try {
      const dateKey = format(approvalModalDate, "yyyy-MM-dd");
      const isAuto = isAutoCheckoutDate(approvalModalDate, approvalReasonEntry.userId);
      await updateNonCheckInReasonStatus(
        organizationId,
        approvalReasonEntry.userId,
        dateKey,
        status,
        currentUser.id,
        isAuto ? "auto checkout" : undefined
      );

      // Update local state
      setNonCheckInReasons((prev) => ({
        ...prev,
        [dateKey]: {
          ...prev[dateKey],
          approvalStatus: status,
          reviewedBy: currentUser.id,
          reviewedAt: new Date().toISOString(),
          ...(isAuto ? { type: "auto checkout" } : {}),
        },
      }));

      // If approved and this is an auto-checkout day, set markedAsAbsent to false
      if (status === "Approved" && isAutoCheckoutDate(approvalModalDate, approvalReasonEntry.userId)) {
        const checkInDateKey = format(approvalModalDate, "yyyy-MM-dd");
        const monthKey = `${approvalModalDate.getFullYear()}-${String(approvalModalDate.getMonth() + 1).padStart(2, "0")}`;
        // Find the check-in record for this date and update markedAsAbsent
        const monthColRef = collection(
          db,
          `organization/${organizationId}/CheckInCheckOutData/${approvalReasonEntry.userId}/${monthKey}`
        );
        const snapshot = await getDocs(monthColRef);
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          if (data.checkInTime) {
            const recordDate = format(parseISO(data.checkInTime), "yyyy-MM-dd");
            if (recordDate === checkInDateKey && data.markedAsAbsent === true) {
              await updateDoc(docSnap.ref, { markedAsAbsent: false });
              // Only remove from markedAbsent so the day shows as checked-in.
              // Keep in autoCheckoutDates so the auto-checkout indicator persists.
              setUserMarkedAbsentDates((prev) => {
                const arr = prev[approvalReasonEntry.userId] || [];
                return {
                  ...prev,
                  [approvalReasonEntry.userId]: arr.filter((d) => d !== checkInDateKey),
                };
              });
              break;
            }
          }
        }
      }

      showToast({ title: `Reason ${status.toLowerCase()}`, status: "success" });
      setApprovalModalDate(null);
      setApprovalReasonEntry(null);
    } catch (error) {
      console.error("Error updating approval status:", error);
      showToast({ title: "Failed to update status", status: "error" });
    } finally {
      setApproving(false);
    }
  };

  // Helper function to calculate yearly stats (joining-date aware)
  const calculateYearlyStats = (userId: string) => {
    const rawTimes = userTimes[userId] || [];
    // My Check-Ins treats markedAsAbsent auto-checkouts as absent, so they
    // must drop out of the check-in tallies too, not just the calendar tile.
    const times = allowReasonEntry
      ? rawTimes.filter((dt) => !isMarkedAbsentDate(dt, userId))
      : rawTimes;
    const user = users.find((u) => u.id === userId);
    const userJoin = (() => {
      if (!user?.joiningDate) return null;
      try {
        const jd = parseISO(user.joiningDate);
        return isNaN(jd.getTime()) ? null : jd;
      } catch {
        return null;
      }
    })();

    // If selected year is before joining year, or a future year, return zeros
    if (
      (userJoin && calendarYear < userJoin.getFullYear()) ||
      calendarYear > now.getFullYear()
    ) {
      return {
        bizDays: 0,
        checkedInDays: 0,
        missedDays: 0,
        attendanceRate: 0,
        sundayCheckins: 0,
        holidayCheckins: 0,
        autoCheckouts: 0,
      };
    }
    const yearStart = startOfYear(new Date(calendarYear, 0));
    const yearEnd =
      calendarYear === now.getFullYear() ? now : endOfYear(yearStart);

    // Start counting from user's joining date if present
    let statsStart = yearStart;
    if (user?.joiningDate) {
      try {
        const jd = parseISO(user.joiningDate);
        if (!isNaN(jd.getTime()) && isAfter(jd, statsStart)) {
          statsStart = jd;
        }
      } catch { }
    }

    const bizDays = eachDayOfInterval({
      start: statsStart,
      end: yearEnd,
    }).filter((d) => {
      const dateKey = format(d, "yyyy-MM-dd");
      return !isSunday(d) && !holidays[dateKey]; // Exclude Sundays and holidays
    });

    // Build a set of unique check-in dates within the year range
    const inYearCheckins = times.filter(
      (dt) =>
        (isAfter(dt, statsStart) ||
          dt.toDateString() === statsStart.toDateString()) &&
        (isBefore(dt, yearEnd) || dt.toDateString() === yearEnd.toDateString())
    );
    const uniqueCheckinKeys = new Set(
      inYearCheckins.map((dt) => dt.toDateString())
    );

    // Checked-in on business days (excluding Sundays and holidays)
    const checkedInOnBizDays = bizDays.filter((d) =>
      uniqueCheckinKeys.has(d.toDateString())
    );

    // Count Sunday and holiday check-ins (extra, not part of business days)
    const sundayCheckinsCount = inYearCheckins.reduce(
      (acc, dt) => acc + (isSunday(dt) ? 1 : 0),
      0
    );
    const holidayCheckinsCount = inYearCheckins.reduce((acc, dt) => {
      const dateKey = format(dt, "yyyy-MM-dd");
      return acc + (holidays[dateKey] ? 1 : 0);
    }, 0);

    // Auto checkouts within year (by check-in date) - only count days also
    // flagged markedAsAbsent, matching the calendar tile indicator.
    const markedAbsentSet = new Set(userMarkedAbsentDates[userId] || []);
    const autoDates = new Set(
      (userAutoCheckoutDates[userId] || []).filter((key) => markedAbsentSet.has(key))
    );
    const autoCheckouts = Array.from(autoDates).filter((key) => {
      try {
        const dt = parseISO(key);
        return (
          (isAfter(dt, statsStart) ||
            dt.toDateString() === statsStart.toDateString()) &&
          (isBefore(dt, yearEnd) ||
            dt.toDateString() === yearEnd.toDateString())
        );
      } catch {
        return false;
      }
    }).length;

    // Calculate approved leave days within the year
    const userLeaves = userApprovedLeaves[userId] || [];
    const leaveDaysInYear = new Set<string>();

    userLeaves.forEach((leave: any) => {
      const fromDate = new Date(leave.fromDate);
      const toDate = new Date(leave.toDate);

      // Generate all dates in the leave period
      const leaveDates = eachDayOfInterval({ start: fromDate, end: toDate });

      leaveDates.forEach(leaveDate => {
        const leaveDateKey = format(leaveDate, "yyyy-MM-dd");
        // Only count leave days that fall within business days and the year
        if (bizDays.some(bizDay => format(bizDay, "yyyy-MM-dd") === leaveDateKey)) {
          leaveDaysInYear.add(leaveDateKey);
        }
      });
    });

    // Net business days exclude approved leave
    const netBizDays = Math.max(0, bizDays.length - leaveDaysInYear.size);

    // Total check-ins = business-day check-ins + Sunday check-ins + holiday check-ins
    const totalCheckins =
      checkedInOnBizDays.length + sundayCheckinsCount + holidayCheckinsCount;

    const missedDays = Math.max(0, netBizDays - checkedInOnBizDays.length);
    const attendanceRate =
      netBizDays > 0
        ? Math.round((checkedInOnBizDays.length / netBizDays) * 100)
        : 0;

    return {
      bizDays: netBizDays,
      checkedInDays: totalCheckins,
      missedDays,
      attendanceRate,
      sundayCheckins: sundayCheckinsCount,
      holidayCheckins: holidayCheckinsCount,
      autoCheckouts,
    };
  };

  // Render year view calendar
  const renderYearCalendar = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    const isSelf = allowReasonEntry && userId === currentUser?.id;
    let joinDate: Date | null = null;
    if (user?.joiningDate) {
      try {
        const jd = parseISO(user.joiningDate);
        joinDate = isNaN(jd.getTime()) ? null : jd;
      } catch {
        joinDate = null;
      }
    }
    const months = [];
    for (let month = 0; month < 12; month++) {
      const monthStart = startOfMonth(new Date(calendarYear, month));
      // Clamped to today for the current month — used to stop future days from
      // being clicked to add a non-check-in reason.
      const monthEnd =
        calendarYear === now.getFullYear() && month === now.getMonth()
          ? now
          : endOfMonth(monthStart);
      // The real last day of the month. Tile styling must use this, not the
      // clamped monthEnd, or approved/pending leave and holidays falling later
      // this month render as plain days.
      const monthLastDay = endOfMonth(monthStart);

      months.push(
        <div
          key={month}
          className={`month-calendar ${calendarYear === now.getFullYear() && month === now.getMonth()
            ? "current-month"
            : ""
            }`}
        >
          <div className="month-calendar-header">{MONTHS[month]}</div>
          <Calendar
            calendarType="iso8601"
            activeStartDate={monthStart}
            tileClassName={({ date, view }) => {
              if (view !== "month") return "";
              if (date < monthStart || date > monthLastDay) return "";
              if (date.getMonth() !== month) return "";
              // Before joining date → normal color (no special class)
              if (joinDate && date < joinDate) return "";
              const checkedIn = isEffectivelyCheckedIn(date, userId);
              // Auto-checkout indicator: shown when the day has been flagged markedAsAbsent
              // OR when the NonCheckInReason has type "auto checkout" (persists after approval/rejection)
              const dateKeyTile = format(date, "yyyy-MM-dd");
              const reasonEntryForAuto = nonCheckInReasons[dateKeyTile];
              const auto = isAutoCheckoutDate(date, userId) && (isMarkedAbsentDate(date, userId) || reasonEntryForAuto?.type === "auto checkout");
              const holiday = isHoliday(date);
              const approvedLeave = isApprovedLeave(date, userId);
              const pendingLeave = isPendingLeave(date, userId);

              if (isSunday(date)) {
                return checkedIn ? "react-calendar__tile--sundaycheckedin" : "";
              }
              if (holiday) {
                return checkedIn
                  ? "react-calendar__tile--holidaycheckedin"
                  : "react-calendar__tile--holiday";
              }
              // Only a WHOLE day off claims the tile's circle. A half day is
              // carried by the small corner marker instead, leaving the tile
              // free to show whether the other half was actually worked.
              const approvedPortion = approvedLeave
                ? getApprovedLeavePortion(date, userId)
                : null;
              const pendingPortion = pendingLeave
                ? getPendingLeavePortion(date, userId)
                : null;
              const halfDayLeave =
                (approvedLeave && approvedPortion !== "full") ||
                (pendingLeave && pendingPortion !== "full");

              if (approvedLeave && approvedPortion === "full") {
                return "react-calendar__tile--approvedleave";
              }
              if (pendingLeave && pendingPortion === "full") {
                return "react-calendar__tile--pendingleave";
              }
              if (checkedIn) {
                return auto
                  ? "react-calendar__tile--checkedin autocheckout-ring"
                  : "react-calendar__tile--checkedin";
              }
              if (date > now) return "react-calendar__tile--upcoming";
              // A half day off with no check-in is not a missed day —
              // isMissedDay() already says so, and a red tile would contradict it.
              if (halfDayLeave) return "";
              // Auto-checkout dates are treated as missed but still show the red border ring
              if (auto) {
                // Admin: clickable only if reason is pending
                if (!isSelf) {
                  const dateKey = format(date, "yyyy-MM-dd");
                  const entry = nonCheckInReasons[dateKey];
                  if (entry && (!entry.approvalStatus || entry.approvalStatus === "Pending")) {
                    return "react-calendar__tile--checkedin autocheckout-ring clickable-missed";
                  }
                  return "react-calendar__tile--checkedin autocheckout-ring";
                }
                return "react-calendar__tile--missed autocheckout-ring clickable-missed";
              }
              // Admin tiles with pending reasons are clickable for approval
              if (!isSelf) {
                const dateKey = format(date, "yyyy-MM-dd");
                const entry = nonCheckInReasons[dateKey];
                if (entry && (!entry.approvalStatus || entry.approvalStatus === "Pending")) {
                  return "react-calendar__tile--missed clickable-missed";
                }
              }
              return isSelf ? "react-calendar__tile--missed clickable-missed" : "react-calendar__tile--missed";
            }}
            onClickDay={(date) => {
              if (date < monthStart || date > monthEnd) return;
              if (date.getMonth() !== month) return;
              if (joinDate && date < joinDate) return;
              const dateKey = format(date, "yyyy-MM-dd");
              const isAuto = isAutoCheckoutDate(date, userId);
              const reasonEntry = nonCheckInReasons[dateKey];
              // Admin: only open modal for pending reasons (not already approved/rejected)
              if (!isSelf && reasonEntry) {
                if (!reasonEntry.approvalStatus || reasonEntry.approvalStatus === "Pending") {
                  openApprovalModal(date);
                }
                return;
              }
              if (!isMissedDay(date, userId)) return;
              // User can only add reason once - don't open modal if reason exists
              if (isSelf && reasonEntry) return;
              openReasonModal(date);
            }}
            tileContent={({ date, view }) => {
              if (view !== "month") return null;
              if (date.getMonth() !== month) return null;
              // Before joining date → no extra content/badges
              if (joinDate && date < joinDate) return null;
              const key = format(date, "yyyy-MM-dd");
              const info = userDayRanges[userId]?.[key];
              const checkedIn = isEffectivelyCheckedIn(date, userId);
              // Auto-checkout indicator: shown when the day has been flagged markedAsAbsent
              // OR when the NonCheckInReason has type "auto checkout" (persists after approval/rejection)
              const reasonEntryForAutoTile = nonCheckInReasons[key];
              const auto = isAutoCheckoutDate(date, userId) && (isMarkedAbsentDate(date, userId) || reasonEntryForAutoTile?.type === "auto checkout");
              const holiday = isHoliday(date);
              const approvedLeave = isApprovedLeave(date, userId);
              const leaveReason = getLeaveReason(date, userId);
              const pendingLeave = isPendingLeave(date, userId);
              const pendingLeaveReason = getPendingLeaveReason(date, userId);
              // WFH indicators from WorkFromHomeData
              const approvedWfhDay = isApprovedWfh(date, userId);
              const pendingWfhDay = isPendingWfh(date, userId);
              const wfhReason = getWfhReason(date, userId);
              // Half-day leave: which half, and the colour of the owning tile.
              const leavePortion = approvedLeave
                ? getApprovedLeavePortion(date, userId)
                : pendingLeave
                  ? getPendingLeavePortion(date, userId)
                  : null;
              const isHalfDayLeave = leavePortion === "first" || leavePortion === "second";
              const holidayCheckedIn = checkedIn && (isSunday(date) || holiday);
              const missedDay = isMissedDay(date, userId);
              const missedReasonEntry = nonCheckInReasons[key];
              const startStr = info?.start ? format(info.start, "p") : "—";
              const endStr = info?.end ? format(info.end, "p") : "—";
              const totalHrs = info?.totalMs != null ? info.totalMs / 3600000 : null;
              const totalStr = totalHrs != null ? `${totalHrs.toFixed(2)} hrs` : "—";
              const isHalfDay = checkedIn && totalHrs != null && totalHrs <= 5;
              const isUnder8 = checkedIn && totalHrs != null && totalHrs < 8;
              const isGood = checkedIn && totalHrs != null && totalHrs >= 8 && totalHrs <= 9;
              const isBurnout = checkedIn && totalHrs != null && totalHrs > 9;
              const statusLabel = !checkedIn || totalHrs == null
                ? null
                : isUnder8
                  ? "Under working"
                  : isGood
                    ? "Good"
                    : "Burnout";

              return (
                <div className="absolute inset-0 group">
                  {/* Holiday: blue square covering the day number */}
                  {holiday && !checkedIn && (
                    <div className="absolute inset-0 flex items-center justify-center z-30">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white text-[10px] font-semibold rounded">
                        {date.getDate()}
                      </span>
                    </div>
                  )}
                  {/* Holiday CheckIn: amber square covering the day number (checked in on a holiday or Sunday) */}
                  {holidayCheckedIn && (
                    <div className="absolute inset-0 flex items-center justify-center z-30">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-amber-400 text-white text-[10px] font-semibold rounded">
                        {date.getDate()}
                      </span>
                    </div>
                  )}
                  {/* Half-day LEAVE marker: a half-filled dot, filled on the
                      side of the day that is taken off. Blue = approved,
                      orange = pending, matching the tile colour. */}
                  {isHalfDayLeave && (
                    <div className="pointer-events-none absolute left-1 top-1 z-40">
                      <span
                        className={`relative inline-block w-[7px] h-[7px] rounded-full overflow-hidden align-middle border ${
                          approvedLeave
                            ? "bg-blue-500 border-blue-600"
                            : "bg-orange-500 border-orange-600"
                        }`}
                      >
                        <span
                          className={`absolute top-0 h-full w-1/2 bg-white ${
                            leavePortion === "first" ? "right-0" : "left-0"
                          }`}
                        ></span>
                      </span>
                    </div>
                  )}
                  {/* Half-day CHECK-IN marker: a half-filled green dot in the
                      top-left corner, matching the style of the half-day leave marker.
                      Shows when total worked hours <= 5. */}
                  {isHalfDay && (
                    <div className="pointer-events-none absolute left-1 top-1 z-40">
                      <span className="relative inline-block w-[7px] h-[7px] bg-green-500 rounded-full overflow-hidden align-middle border border-green-600">
                        <span className="absolute right-0 top-0 h-full w-1/2 bg-white"></span>
                      </span>
                    </div>
                  )}
                  {/* WFH indicator: purple dot when there's a WFH entry in WorkFromHomeData */}
                  {checkedIn && (approvedWfhDay || pendingWfhDay) && (
                    <div className="pointer-events-none absolute right-1 top-1 z-40">
                      <span className="inline-block w-[7px] h-[7px] bg-purple-500 rounded-full border border-purple-600"></span>
                    </div>
                  )}
                  {/* WFH from WorkFromHomeData indicator: purple dot for approved/pending (non-checked-in days) */}
                  {!checkedIn && (approvedWfhDay || pendingWfhDay) && (
                    <div className="pointer-events-none absolute right-1 top-1 z-40">
                      <span className="inline-block w-[7px] h-[7px] rounded-full border bg-purple-500 border-purple-600"></span>
                    </div>
                  )}
                  {/* Under 8 hours exclamatory badge */}
                  {isUnder8 && (
                    <div className="pointer-events-none absolute right-2 bottom-2 z-40">
                      <span className="text-red-500 text-[12px] leading-none font-extrabold select-none">!</span>
                    </div>
                  )}
                  {/* Missed-day reason indicator */}
                  {(missedDay || auto) && missedReasonEntry && (
                    <div className="pointer-events-none absolute right-1 top-1 z-40">
                      <span className="inline-block w-[6px] h-[6px] bg-slate-500 rounded-full"></span>
                    </div>
                  )}
                  <div className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-l border-t border-gray-300 rotate-45"></div>
                    <div className="relative bg-white/95 backdrop-blur-sm text-black text-[10px] sm:text-xs rounded-lg shadow-xl px-3 py-2 whitespace-nowrap border border-gray-300 ring-1 ring-gray-200">
                      {holiday && (
                        <div className="mb-0.5">
                          <span className="font-semibold">Holiday:</span>{" "}
                          {holidays[key]?.reason || "Holiday"}
                        </div>
                      )}
                      {approvedLeave && (
                        <div className="mb-0.5">
                          <span className="font-semibold text-blue-600">Approved Leave:</span>{" "}
                          <span className="text-blue-700">{leaveReason || "Leave"}</span>
                          {leavePortion && (
                            <span className="ml-1 text-blue-700">
                              ({DAY_PORTION_LABEL[leavePortion]})
                            </span>
                          )}
                        </div>
                      )}
                      {pendingLeave && (
                        <div className="mb-0.5">
                          <span className="font-semibold text-orange-600">Pending Leave:</span>{" "}
                          <span className="text-orange-700">{pendingLeaveReason || "Leave"}</span>
                          {leavePortion && (
                            <span className="ml-1 text-orange-700">
                              ({DAY_PORTION_LABEL[leavePortion]})
                            </span>
                          )}
                        </div>
                      )}
                      {(approvedWfhDay || pendingWfhDay) && (
                        <div className="mb-0.5">
                          <span className="font-semibold text-purple-600">Remote Work:</span>{" "}
                          <span className="text-purple-700">{wfhReason || "Remote Work"}</span>
                          <div className={`text-[10px] font-semibold ${approvedWfhDay ? "text-green-600" : "text-orange-600"}`}>
                            {approvedWfhDay ? "Approved" : "Pending"}
                          </div>
                        </div>
                      )}
                      {checkedIn ? (
                        <div className="flex flex-col items-start gap-1">
                          <div><span className="text-gray-500">Start</span>: <span className="font-medium">{startStr}</span></div>
                          <div><span className="text-gray-500">End</span>: <span className="font-medium">{endStr}</span></div>
                          <div className="flex items-center gap-2">
                            <span><span className="text-gray-500">Total</span>: <span className="font-medium">{totalStr}</span></span>
                            {auto && (
                              <span className="px-1 rounded bg-rose-100 text-rose-700 border border-rose-200">Auto</span>
                            )}
                          </div>
                          {info?.workMode && (
                            <div><span className="text-gray-500">Mode</span>: <span className="font-medium text-[#B59143]">{info.workMode}</span></div>
                          )}
                          {statusLabel && (
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500">Status</span>:
                              <span className={
                                isUnder8
                                  ? "font-semibold text-amber-700"
                                  : isGood
                                    ? "font-semibold text-green-700"
                                    : "font-semibold text-rose-700"
                              }>
                                {statusLabel}
                              </span>
                            </div>
                          )}
                          {isHalfDay && (
                            <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                              <span className="inline-block w-[10px] h-[10px] bg-green-500 rounded-full relative overflow-hidden border border-green-600">
                                <span className="absolute right-0 top-0 h-full w-1/2 bg-white"></span>
                              </span>
                              <span className="font-medium text-gray-700">Half Day</span>
                            </div>
                          )}
                          {/* Show reason + approval status for auto-checkout days with a reason */}
                          {auto && missedReasonEntry && (
                            <div className="mt-1 border-t border-gray-200 pt-1 w-full">
                              <span className="font-semibold text-gray-700">Reason:</span>{" "}
                              <span>{missedReasonEntry.reason}</span>
                              {/* Show approval status for both admin and user */}
                              {missedReasonEntry.approvalStatus && (
                                <div className="mt-1">
                                  <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                    missedReasonEntry.approvalStatus === "Approved"
                                      ? "bg-green-100 text-green-700 border border-green-200"
                                      : missedReasonEntry.approvalStatus === "Rejected"
                                        ? "bg-red-100 text-red-700 border border-red-200"
                                        : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                  }`}>
                                    {missedReasonEntry.approvalStatus}
                                  </span>
                                </div>
                              )}
                              {!isSelf && (!missedReasonEntry.approvalStatus || missedReasonEntry.approvalStatus === "Pending") && (
                                <div className="mt-1 text-[10px] text-blue-600 italic cursor-pointer">
                                  Click To Approve/Reject reason
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : !holiday && !approvedLeave && !pendingLeave ? (
                        <div>
                          {date > now ? null : (
                            <>
                              <div>{auto ? "Auto Checkout" : "No check-in"}</div>
                              {missedReasonEntry ? (
                                <div className="mt-1">
                                  <span className="font-semibold text-gray-700">Reason:</span>{" "}
                                  <span>{missedReasonEntry.reason}</span>
                                  {/* Show approval status for users */}
                                  {isSelf && missedReasonEntry.approvalStatus && (
                                    <div className="mt-1">
                                      <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                        missedReasonEntry.approvalStatus === "Approved"
                                          ? "bg-green-100 text-green-700 border border-green-200"
                                          : missedReasonEntry.approvalStatus === "Rejected"
                                            ? "bg-red-100 text-red-700 border border-red-200"
                                            : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                      }`}>
                                        {missedReasonEntry.approvalStatus}
                                      </span>
                                    </div>
                                  )}
                                  {/* Show approval status for admin */}
                                  {!isSelf && missedReasonEntry.approvalStatus && (
                                    <div className="mt-1">
                                      <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                        missedReasonEntry.approvalStatus === "Approved"
                                          ? "bg-green-100 text-green-700 border border-green-200"
                                          : missedReasonEntry.approvalStatus === "Rejected"
                                            ? "bg-red-100 text-red-700 border border-red-200"
                                            : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                      }`}>
                                        {missedReasonEntry.approvalStatus}
                                      </span>
                                    </div>
                                  )}
                                  {/* Show click to approve/reject for admin only if pending */}
                                  {!isSelf && (!missedReasonEntry.approvalStatus || missedReasonEntry.approvalStatus === "Pending") && (
                                    <div className="mt-1 text-[10px] text-blue-600 italic cursor-pointer">
                                      Click To Approve/Reject reason
                                    </div>
                                  )}
                                </div>
                              ) : isSelf && missedDay ? (
                                <div className="mt-1 text-[10px] text-blue-600 italic">Click to add a reason</div>
                              ) : null}
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            }}
            showNavigation={false}
          />
        </div>
      );
    }
    return months;
  };

  // If TimeSheets mode is selected, render TimeSheetAlert component
  if (viewMode === "TimeSheets") {
    return (
      <TimeSheetAlert
        key={`${selectedYear}-${selectedMonth}`} // ⬅️ optional: clean remount
        organizationId={organizationId}
        users={users}
        currentUser={currentUser}
        onChangeMode={(mode) => {
          if (mode === "CheckIn") {
            // clear any stale TS data if you want
            setMissedCount({});
            setUserTimes({});
            setViewMode("CheckIn"); // ⬅️ actually switch back
          }
        }}
      />
    );
  }

  if (viewMode === "CheckInTable") {
    return (
      <CheckInTable
        organizationId={organizationId}
        users={users}
        onBack={() => setViewMode("CheckIn")}
      />
    );
  }

  if (viewMode === "WFH") {
    return (
      <div className="w-full h-full flex flex-col py-0 px-4 min-h-0">
        <div className={`flex justify-between items-center p-4 min-h-[64px] overflow-visible ${isDark ? 'relative overflow-hidden bg-gradient-to-br from-[#0B0B0F] via-[#1A1C1F] to-[#0B0B0F] ring-1 ring-[#2E2F33] shadow-[inset_0_1px_0_rgba(251,210,130,0.08),inset_0_-1px_0_rgba(251,210,130,0.05),0_10px_28px_rgba(0,0,0,0.45)]' : 'bg-gradient-to-r from-[#FFF7E6] via-[#FAEDD0] to-[#FFF8EA] shadow-[inset_0_-1px_0_0_rgba(229,231,235,0.7),0_2px_8px_rgba(181,145,67,0.08)]'} rounded-md mb-4 border ${isDark ? 'border-[#2E2F33]' : 'border-[#E5E7EB]'} flex-shrink-0`}>
          {isDark && (
            <>
              <span className="pointer-events-none absolute inset-0 [background:radial-gradient(120%_60%_at_50%_120%,rgba(181,145,67,0.18)_0%,rgba(0,0,0,0)_60%)]" />
              <span className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FBD282]/35 to-transparent" />
              <span className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#B59143]/25 to-transparent blur-[2px]" />
              <span className="pointer-events-none absolute -left-10 top-1/2 h-24 w-1/2 -translate-y-1/2 -rotate-6 bg-gradient-to-r from-transparent via-[#FBD282]/14 to-transparent blur-sm" />
            </>
          )}
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-0">
              <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Remote Work</span>
              <span className={`text-xs ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Apply for remote work and track your requests.</span>
            </div>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <WfhApply
            organizationId={organizationId}
            userId={currentUser?.id}
            currentUser={currentUser}
          />
        </div>
      </div>
    );
  }

  // — Calendar view for single user —
  if (calendarUserId) {
    const user = users.find((u) => u.id === calendarUserId);

    // users may still be loading (e.g. My Check-Ins opens straight to
    // initialUserId before the users list has arrived) - wait rather than crash.
    if (!user) {
      return (
        <div className={`flex flex-col w-full h-full min-h-screen items-center justify-center gap-3 ${isDark ? 'bg-[#0B0B0F] text-white' : 'bg-gray-50 text-black'}`}>
          <Skeleton
            height="32px"
            width="220px"
            startColor={isDark ? "gray.700" : "gray.200"}
            endColor={isDark ? "gray.600" : "gray.300"}
            borderRadius="md"
          />
        </div>
      );
    }

    const stats = calculateYearlyStats(calendarUserId);

    return (
      <div className={`flex flex-col w-full h-full min-h-screen ${isDark ? 'bg-[#0B0B0F] text-white' : 'bg-gray-50 text-black'}`}>
        {/* Enhanced Header */}
        <div className={`${isDark ? 'bg-[#1A1C1F] border-b border-[#2E2F33]' : 'bg-white shadow-sm border-b'}`}>
          <div className="px-6 py-4">
            <div className="flex items-center gap-4 mb-3">
              {!hideBackButton && (
                <button
                  onClick={() => {
                    if (onBackToCalendar) {
                      onBackToCalendar();
                    } else {
                      setCalendarUserId(null);
                      setCalendarYear(new Date().getFullYear());
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-1 border rounded-lg transition-all duration-200 font-medium ${isDark ? 'border-[#B59143] text-[#FBD282] hover:bg-[#2A3441]' : 'border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]'}`}
                  aria-label="Go back"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back
                </button>
              )}
              <div className="flex items-center gap-3">
                {loading ? (
                  <>
                    <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                    <div className="space-y-2">
                      <div className="h-8 bg-gray-300 rounded w-40 animate-pulse"></div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`${'bg-[#B59143] text-white'} w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm`}>
                      {user.firstName.charAt(0)}
                      {user.lastName.charAt(0)}
                    </div>
                    <div>
                      <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.firstName} {user.lastName}
                      </h1>
                    </div>
                  </>
                )}
              </div>
              {/* Year Selection Only */}
              <div className="ml-auto flex items-center gap-3">
                <CustomDropdown
                  value={calendarYear.toString()}
                  onChange={(value) => setCalendarYear(+value)}
                  isDark={isDark}
                  className="px-4 py-2 rounded-lg text-sm font-medium w-32"
                  options={yearOptions.map((year) => ({
                    value: year.toString(),
                    label: year.toString()
                  }))}
                />
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-8 mt-4">
              {loading ? (
                // Loading skeleton for stats cards
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-gray-100 p-4 rounded-lg border border-gray-200 animate-pulse"
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-300 rounded w-20"></div>
                        <div className="h-8 bg-gray-300 rounded w-16"></div>
                      </div>
                      <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-600 text-sm font-medium">
                          Check-ins
                        </p>
                        <p className="text-xl font-bold text-green-700">
                          {stats.checkedInDays}
                        </p>
                      </div>
                      {/* <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div> */}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-rose-100 p-4 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-600 text-sm font-medium">
                          Missed Days
                        </p>
                        <p className="text-xl font-bold text-red-700">
                          {stats.missedDays}
                        </p>
                      </div>
                      {/* <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div> */}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-600 text-sm font-medium">
                          Business Days
                        </p>
                        <p className="text-xl font-bold text-blue-700">
                          {stats.bizDays}
                        </p>
                      </div>
                      {/* <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <CalendarIcon className="w-4 h-4 text-white" />
                      </div> */}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-violet-100 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-600 text-sm font-medium">
                          Attendance Rate
                        </p>
                        <p className="text-xl font-bold text-purple-700">
                          {stats.attendanceRate}%
                        </p>
                      </div>
                      {/* <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <div className="text-white text-xs font-bold">%</div>
                      </div> */}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-fuchsia-50 to-pink-100 p-4 rounded-lg border border-fuchsia-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-fuchsia-600 text-sm font-medium">
                          Auto Checkouts
                        </p>
                        <p className="text-xl font-bold text-fuchsia-700">
                          {stats.autoCheckouts}
                        </p>
                      </div>
                      {/* <div className="w-8 h-8 bg-fuchsia-500 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div> */}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-yellow-100 p-4 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-600 text-sm font-medium">
                          Holiday CheckIns
                        </p>
                        <p className="text-xl font-bold text-amber-700">
                          {stats.sundayCheckins + stats.holidayCheckins}
                        </p>
                      </div>
                      {/* <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div> */}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Calendar Section */}
        <div className="flex-1 p-6">
          <div className="max-w-8xl mx-auto">
            <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white'} rounded-sm overflow-hidden`}>
              {/* Calendar Header */}
              <div className={`px-6 py-4 ${theme.headerGradient}`}>
                <div className="flex items-center justify-between">
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                    Attendance Calendar - {calendarYear}
                  </h2>
                  <div className="flex-1 min-w-0">
                    {/* Legend — centred, wraps onto as many rows as it needs.
                        Label text is fluid so it shrinks rather than overflowing
                        when the header gets narrow. */}
                    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 sm:gap-x-4">
                      {LEGEND_ITEMS.map(({ key, swatch, label }) => (
                        <div key={key} className="flex items-center gap-1.5 shrink-0">
                          {swatch}
                          <span
                            className={`${isDark ? 'text-white' : 'text-black'} font-medium whitespace-nowrap text-[clamp(9px,0.72vw,14px)]`}
                          >
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Calendar Styles */}
              <style>
                {`
                  .react-calendar__tile abbr {
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: auto !important;
                    height: auto !important;
                    border-radius: 0 !important;
                    transition: color 0.15s ease !important;
                    background: transparent !important;
                    font-size: 0.9em !important; /* slight bump for the date number */
                  }
                  .month-calendar {
                    background: ${isDark ? '#1A1C1F' : '#ffffff'} !important;
                  }
                  .month-calendar .react-calendar__month-view__weekdays {
                    background: ${isDark ? '#14161B' : '#f8fafc'} !important;
                    border-bottom: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'} !important;
                    color: ${isDark ? '#C3C9D3' : '#64748b'} !important;
                  }
                  .month-calendar .react-calendar__month-view__days {
                    background: ${isDark ? '#1A1C1F' : '#ffffff'} !important;
                  }
                  .month-calendar-header {
                    background: ${isDark ? '#14161B' : '#f8fafc'} !important;
                    padding: 0.5rem !important;
                    border-bottom: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'} !important;
                    text-align: center !important;
                    font-weight: 700 !important;
                    color: ${isDark ? '#FFFFFF' : '#111827'} !important;
                    font-size: 0.8rem !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                  }
                  /* Center tile content to ensure date circle sits in the middle */
                  .react-calendar__tile {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    overflow: visible !important;
                  }
                  /* Allow tooltips to overflow the month box */
                  .month-calendar { overflow: visible !important; }
                  .month-calendar .react-calendar__month-view__days { overflow: visible !important; }
                  .month-calendar .react-calendar__tile { overflow: visible !important; }
                  /* Remove outer ring; we'll draw a subtle inline border on the number instead */
                  .autocheckout-ring abbr { box-shadow: none !important; }
                  .react-calendar__tile--missed abbr {
                    background: transparent !important;
                    color: #dc2626 !important; /* red-600 */
                    font-weight: 700 !important;
                  }
                  .clickable-missed {
                    cursor: pointer !important;
                  }
                  .clickable-missed:hover abbr {
                    text-decoration: underline !important;
                  }
                  .react-calendar__tile--checkedin abbr {
                    background: transparent !important;
                    color: #16a34a !important; /* green-600 */
                    font-weight: 700 !important;
                  }
                  .react-calendar__tile--autocheckout abbr { /* legacy, unused now */ }
                  .react-calendar__tile--sundaycheckedin abbr {
                    background: transparent !important;
                    color: #f59e0b !important; /* amber-500 */
                    border: none !important;
                    font-weight: 700 !important;
                  }
                  .react-calendar__tile--holiday abbr {
                    background: ${isDark ? '#B59143' : '#3b82f6'} !important;
                    color: white !important;
                    border: none !important;
                    border-radius: 50% !important;
                    font-weight: 600 !important;
                    padding: 0 !important;
                    width: 24px !important;
                    height: 24px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    font-size: 0.8em !important;
                  }
                  .react-calendar__tile--pendingleave abbr {
                    background: #f97316 !important;
                    color: white !important;
                    border: none !important;
                    border-radius: 50% !important;
                    font-weight: 600 !important;
                    padding: 0 !important;
                    width: 24px !important;
                    height: 24px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    font-size: 0.8em !important;
                  }

                  .react-calendar__tile--upcoming {
                    background: #f8fafc !important;
                    color: #94a3b8 !important;
                    border: 1px solid #e2e8f0 !important;
                  }
                  .big-calendar .react-calendar__tile--missed {
                    background: ${isDark ? '#14161B' : '#ffffff'} !important;
                    border: 1px solid ${isDark ? '#2E2F33' : '#e5e7eb'} !important;
                    color: ${isDark ? '#FFFFFF' : '#374151'} !important;
                  }
                  .big-calendar .react-calendar__tile--checkedin {
                    background: ${isDark ? '#14161B' : '#ffffff'} !important;
                    color: ${isDark ? '#FFFFFF' : '#374151'} !important;
                    border: 1px solid ${isDark ? '#2E2F33' : '#e5e7eb'} !important;
                  }
                  .big-calendar .react-calendar__tile--autocheckout { /* legacy, unused now */ }
                  .big-calendar .react-calendar__tile--holiday abbr {
                    background: ${isDark ? '#B59143' : '#3b82f6'} !important;
                    color: white !important;
                    border-radius: 50% !important;
                    font-weight: 600 !important;
                  }
                  .big-calendar .react-calendar__tile--approvedleave {
                    background: #3b82f6 !important;
                    color: white !important;
                    border: 1px solid #2563eb !important;
                    font-weight: 600 !important;
                  }
                  .big-calendar .react-calendar__tile--pendingleave {
                    background: #f97316 !important;
                    color: white !important;
                    border: 1px solid #ea580c !important;
                    font-weight: 600 !important;
                  }
                  .big-calendar .react-calendar__tile--missed:hover,
                  .big-calendar .react-calendar__tile--checkedin:hover,
                  .big-calendar .react-calendar__tile--holiday:hover,
                  .big-calendar .react-calendar__tile--approvedleave:hover,
                  .big-calendar .react-calendar__tile--pendingleave:hover {
                    background: #f8fafc !important;
                    transform: none !important;
                    box-shadow: none !important;
                  }
                `}
              </style>

              {/* Calendar Body */}
              <div className="p-8">
                {loading ? (
                  <div className="year-calendar">
                    {Array.from({ length: 12 }).map((_, mIdx) => (
                      <div
                        key={mIdx}
                        className={`month-calendar p-4 ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-200'} border rounded-lg`}
                      >
                        {/* Month Header Skeleton */}
                        <div className="flex justify-center mb-4">
                          <Skeleton
                            height="16px"
                            width="80px"
                            startColor={isDark ? "gray.700" : "gray.200"}
                            endColor={isDark ? "gray.600" : "gray.300"}
                            borderRadius="md"
                          />
                        </div>

                        {/* Weekdays Row Skeleton */}
                        <div className="grid grid-cols-7 gap-2 mb-3 justify-items-center">
                          {Array.from({ length: 7 }).map((_, wIdx) => (
                            <Skeleton
                              key={wIdx}
                              height="10px"
                              width="20px"
                              startColor={isDark ? "gray.850" : "gray.150"}
                              endColor={isDark ? "gray.700" : "gray.250"}
                              borderRadius="sm"
                            />
                          ))}
                        </div>

                        {/* Days Grid Skeleton */}
                        <div className="grid grid-cols-7 gap-2 justify-items-center">
                          {Array.from({ length: 35 }).map((_, dIdx) => (
                            <Skeleton
                              key={dIdx}
                              height="24px"
                              width="24px"
                              startColor={isDark ? "gray.700" : "gray.200"}
                              endColor={isDark ? "gray.600" : "gray.300"}
                              borderRadius="full"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="year-calendar">
                    {renderYearCalendar(calendarUserId)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal isOpen={!!reasonModalDate} onClose={() => setReasonModalDate(null)} isCentered>
          <ModalOverlay />
          <ModalContent bg={isDark ? "#1A1C1F" : "white"} color={isDark ? "white" : "black"}>
            <ModalHeader>
              Reason for not checking in
              {reasonModalDate && (
                <div className="text-sm font-normal text-gray-400 mt-1">
                  {format(reasonModalDate, "EEEE, MMMM d, yyyy")}
                </div>
              )}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Textarea
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                placeholder="Let your manager know why you didn't check in on this day..."
                rows={4}
                bg={isDark ? "#14161B" : "white"}
                borderColor={isDark ? "#2E2F33" : "gray.200"}
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setReasonModalDate(null)}>
                Cancel
              </Button>
              <Button
                bgGradient="linear(to-r, #FBD282, #E8C16C, #B59143)"
                color={isDark ? "black" : "white"}
                _hover={{ opacity: 0.9 }}
                _active={{ opacity: 0.8 }}
                _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
                onClick={handleSaveReason}
                isDisabled={!reasonInput.trim()}
                isLoading={savingReason}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Approval Modal for Admin */}
        <Modal isOpen={!!approvalModalDate} onClose={() => { setApprovalModalDate(null); setApprovalReasonEntry(null); }} isCentered>
          <ModalOverlay />
          <ModalContent bg={isDark ? "#1A1C1F" : "white"} color={isDark ? "white" : "black"}>
            <ModalHeader>
              Review Non-Check-In Reason
              {approvalModalDate && (
                <div className="text-sm font-normal text-gray-400 mt-1">
                  {format(approvalModalDate, "EEEE, MMMM d, yyyy")}
                </div>
              )}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {approvalReasonEntry && (
                <div>
                  <div className="mb-4">
                    <span className="font-semibold text-gray-700">Reason:</span>
                    <p className="mt-1 text-gray-600">{approvalReasonEntry.reason}</p>
                  </div>
                  {approvalReasonEntry.approvalStatus && approvalReasonEntry.approvalStatus !== "Pending" && (
                    <div className="mb-4">
                      <span className={`inline-block px-2 py-1 text-sm font-medium rounded ${
                        approvalReasonEntry.approvalStatus === "Approved"
                          ? "bg-green-100 text-green-700 border border-green-200"
                          : "bg-red-100 text-red-700 border border-red-200"
                      }`}>
                        {approvalReasonEntry.approvalStatus}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              {(!approvalReasonEntry?.approvalStatus || approvalReasonEntry?.approvalStatus === "Pending") && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApproveReject("Rejected")}
                    disabled={approving}
                    className="px-3 py-1 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApproveReject("Approved")}
                    disabled={approving}
                    className="px-3 py-1 text-xs font-medium text-green-600 border border-green-300 rounded hover:bg-green-50 disabled:opacity-50"
                  >
                    Approve
                  </button>
                </div>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    );
  }

  // — Default list view —
  return (
    <div className="flex flex-col w-full">
      <div className={`w-full px-8 py-4 rounded-lg mb-2 ${theme.headerGradient} border ${theme.border}`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <h1 className="text-2xl font-bold">CheckIn Alerts</h1>
        <p className={`text-xs ${theme.textMuted}`}>
          For each user, the badge shows how many business days (Sundays
          excluded) they missed checking in this month.
        </p>
      </div>

      {/* single container with outer border */}
      <div className={`px-8 mt-0 rounded-md overflow-hidden ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'}`}>
        {/* dropdown section with its own bottom border */}
        <div className={`flex py-3 items-center gap-4 px-4 ${theme.card} border-b ${theme.border} rounded-t-md`}>
          <CustomDropdown
            value={viewMode}
            onChange={(value) => {
              const next = value as "CheckIn" | "TimeSheets" | "WFH";
              if (next === "TimeSheets") {
                // clear CheckIn snapshot so we never render old numbers on return
                setMissedCount({});
                setUserTimes({});
              }
              setViewMode(next);
            }}
            isDark={isDark}
            className="w-40 text-sm rounded px-3 py-2"
            options={[
              { value: "CheckIn", label: "CheckIn" },
              { value: "TimeSheets", label: "TimeSheets" },
              { value: "WFH", label: "Remote Work" }
            ]}
          />

          <CustomDropdown
            value={selectedMonth.toString()}
            onChange={(value) => setSelectedMonth(+value)}
            isDark={isDark}
            className="w-40 text-sm rounded px-3 py-2"
            options={[
              { value: "0", label: "All" },
              ...MONTHS.map((m, i) => ({
                value: (i + 1).toString(),
                label: m
              })).filter((option) => {
                // If current year is selected, only show months up to current month
                if (selectedYear === now.getFullYear()) {
                  const monthIndex = parseInt(option.value) - 1;
                  return monthIndex <= now.getMonth();
                }
                return true; // Show all months for past years
              })
            ]}
          />
          <CustomDropdown
            value={selectedYear.toString()}
            onChange={(value) => setSelectedYear(+value)}
            isDark={isDark}
            className="w-32 text-sm rounded px-3 py-2"
            options={yearOptions.map((year) => ({
              value: year.toString(),
              label: year.toString()
            }))}
          />

          <button
            onClick={() => fetchData(true)}
            className={`p-2.5 rounded transition-colors ${isDark
              ? "bg-[#2E2F33] hover:bg-[#3E4045] text-gray-300 border border-[#444]"
              : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Search and Member Count Section */}
        <div className={`flex justify-between items-center py-3 px-4 ${theme.card} border-b ${theme.border}`}>
          <div className={`relative flex items-center max-w-sm w-full rounded-full ${theme.cardMuted} overflow-hidden`}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
            />
          </div>
          <div className="text-sm text-gray-500">
            Showing{" "}
            {filteredUsers.length}{" "}
            members
          </div>
        </div>

        {loading ? (
          <div className="py-0 overflow-auto">
            <table className={`min-w-full divide-y text-sm ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
              <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Name
                  </th>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Email
                  </th>
                  <th className={`px-4 py-2 text-center text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Missed Days
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm">
                      <Skeleton
                        height="16px"
                        width="120px"
                        startColor={isDark ? "gray.700" : "gray.200"}
                        endColor={isDark ? "gray.600" : "gray.300"}
                        borderRadius="md"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Skeleton
                        height="16px"
                        width="180px"
                        startColor={isDark ? "gray.700" : "gray.200"}
                        endColor={isDark ? "gray.600" : "gray.300"}
                        borderRadius="md"
                      />
                    </td>
                    <td className="px-4 py-3 text-center flex justify-center">
                      <Skeleton
                        height="20px"
                        width="32px"
                        startColor={isDark ? "gray.700" : "gray.200"}
                        endColor={isDark ? "gray.600" : "gray.300"}
                        borderRadius="full"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-0 overflow-auto" style={{ maxHeight: "62vh" }}>
            <table className={`min-w-full divide-y text-sm ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
              <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Name
                  </th>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Email
                  </th>
                  <th className={`px-4 py-2 text-center text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Missed Days
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className={`text-center py-10 font-normal ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}
                    >
                      {searchQuery
                        ? "No members found matching your search."
                        : "No members found."}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr
                      key={u.id}
                      className={`cursor-pointer transition-colors ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}
                      onClick={() => {
                        setCalendarYear(new Date().getFullYear());
                        setCalendarUserId(u.id);
                      }}
                    >
                      <td className="px-4 py-3 text-sm">
                        {u.firstName} {u.lastName}
                      </td>
                      <td className="px-4 py-3 text-sm">{u.email}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {missedCount[u.id] ?? 0}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInAlert;
